/**
 * F-040 — Ammo controller.
 *
 * Per-run finite shot count. Player exhausts ammo by launching planets;
 * once 0 and the simulation has gone quiescent (no flying planets) the
 * run ends. Subscribers (UI counter, game-over check in main.ts) react
 * to changes via the listener bus.
 */

const DEFAULT_AMMO = 30;

export interface AmmoState {
  remaining: number;
  total: number;
}

export interface AmmoController {
  state(): AmmoState;
  /** Decrement on a successful launch. Returns false if already empty. */
  consume(): boolean;
  /** Add bonus ammo (clamped to total). Returns the actual amount granted. */
  refund(amount: number): number;
  isEmpty(): boolean;
  reset(): void;
  subscribe(listener: (state: AmmoState) => void): () => void;
}

export interface AmmoOptions {
  total?: number;
}

export function createAmmoController(options: AmmoOptions = {}): AmmoController {
  const total = options.total ?? DEFAULT_AMMO;
  if (total <= 0) {
    throw new Error("createAmmoController: total must be positive");
  }
  let remaining = total;
  const listeners = new Set<(s: AmmoState) => void>();

  const state = (): AmmoState => ({ remaining, total });

  const announce = (): void => {
    const snap = state();
    for (const l of listeners) l(snap);
  };

  const consume = (): boolean => {
    if (remaining <= 0) return false;
    remaining -= 1;
    announce();
    return true;
  };

  const refund = (amount: number): number => {
    if (amount <= 0) return 0;
    const before = remaining;
    remaining = Math.min(total, remaining + amount);
    const granted = remaining - before;
    if (granted > 0) announce();
    return granted;
  };

  const reset = (): void => {
    remaining = total;
    announce();
  };

  return {
    state,
    consume,
    refund,
    isEmpty: () => remaining <= 0,
    reset,
    subscribe: (l) => {
      listeners.add(l);
      l(state());
      return () => listeners.delete(l);
    },
  };
}

export const __testing = { DEFAULT_AMMO };
